import { useEffect } from 'react';

const DEFAULT_SITE_ORIGIN = 'https://udlejningsvogn.dk';

const getDocumentHead = () => {
    if (typeof document === 'undefined') {
        return null;
    }
    return document.head || document.getElementsByTagName('head')[0] || null;
};

const normalizeBasePath = (value = '') => {
    const trimmed = String(value || '').trim();
    if (!trimmed || trimmed === '/') return '';
    return `/${trimmed.replace(/^\/+|\/+$/g, '')}`;
};

const normalizePath = (value = '/') => {
    const trimmed = String(value || '/').trim();
    if (!trimmed || trimmed === '/') return '/';
    return `/${trimmed.replace(/^\/+/, '')}`;
};

const joinPathParts = (basePath, path) => {
    const normalizedBase = normalizeBasePath(basePath);
    const normalizedPath = normalizePath(path);
    if (!normalizedBase) {
        return normalizedPath;
    }
    if (normalizedPath === '/') {
        return `${normalizedBase}/`;
    }
    return `${normalizedBase}${normalizedPath}`;
};

export const buildAbsoluteUrl = ({ path = '/', basePath = '', origin } = {}) => {
    const rawPath = String(path || '').trim();
    if (/^https?:\/\//i.test(rawPath)) {
        return rawPath;
    }

    const resolvedOrigin = String(origin || (typeof window !== 'undefined' ? window.location.origin : DEFAULT_SITE_ORIGIN)).replace(/\/+$/, '');
    const pathname = joinPathParts(basePath, rawPath || '/');
    return new URL(pathname, `${resolvedOrigin}/`).toString();
};

const ensureHeadElement = (selector, tagName, attributes = {}) => {
    const head = getDocumentHead();
    if (!head) return null;

    let element = head.querySelector(selector);
    if (!element) {
        element = document.createElement(tagName);
        head.appendChild(element);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            element.removeAttribute(key);
            return;
        }
        element.setAttribute(key, value);
    });

    return element;
};

const setMetaTag = (selector, attributes, content) => {
    if (!content) return;
    const element = ensureHeadElement(selector, 'meta', attributes);
    if (element) {
        element.setAttribute('content', content);
    }
};

const replaceStructuredData = (schemaItems) => {
    const head = getDocumentHead();
    if (!head) return;

    head.querySelectorAll('script[data-app-seo-schema="true"]').forEach((node) => node.remove());
    schemaItems
        .filter(Boolean)
        .forEach((item) => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.appSeoSchema = 'true';
            script.text = JSON.stringify(item);
            head.appendChild(script);
        });
};

export const useSeo = ({
    title,
    description,
    path = '/',
    basePath = '',
    imagePath = '/udlejningsvogn-logo.jpeg',
    robots = 'index,follow',
    type = 'website',
    schema = [],
    locale = 'en_US'
} = {}) => {
    useEffect(() => {
        if (typeof document === 'undefined' || !title || !description) {
            return;
        }

        const canonicalUrl = buildAbsoluteUrl({ path, basePath });
        const imageUrl = buildAbsoluteUrl({ path: imagePath, basePath });

        document.documentElement.lang = locale === 'da_DK' ? 'da' : 'en';
        document.title = title;
        setMetaTag('meta[name="description"]', { name: 'description' }, description);
        setMetaTag('meta[name="robots"]', { name: 'robots' }, robots);
        setMetaTag('meta[property="og:title"]', { property: 'og:title' }, title);
        setMetaTag('meta[property="og:description"]', { property: 'og:description' }, description);
        setMetaTag('meta[property="og:type"]', { property: 'og:type' }, type);
        setMetaTag('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
        setMetaTag('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
        setMetaTag('meta[property="og:locale"]', { property: 'og:locale' }, locale);
        setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
        setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
        setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
        setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);

        ensureHeadElement('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonicalUrl });
        replaceStructuredData(Array.isArray(schema) ? schema : [schema]);
    }, [basePath, description, imagePath, locale, path, robots, schema, title, type]);
};
